import * as React from "react";
import { PolymorphicPlayerCard, PolymorphicPlayerCardWidget } from "../../../../../services/bedrock";
import { useContext, useMemo, useRef } from "react";
import { Accordion, Icon, Popup, SemanticICONS } from "semantic-ui-react";
import HeaderWidgetSettings from "./HeaderWidgetSettings";
import FooterWidgetSettings from "./FooterWidgetSettings";
import LinkWidgetSettings from "./LinkWidgetSettings";
import { useFormContext } from "react-hook-form";
import ContentPageWidgetSettings from "./ContentPageWidgetSettings";
import BlogWidgetSettings from "./BlogWidgetSettings";
import SocialMediaWidgetSettings from "./SocialMediaWidgetSettings";
import PostWidgetSettings from "./PostWidgetSettings";
import { getCountMetricsText, getWidgetSubtitle, getWidgetTitle } from "../../../utils";
import { PlayerCardSettingsContext } from "../../../Contexts";
import PhoneWidgetSettings from "./PhoneWidgetSettings";
import EmailWidgetSettings from "./EmailWidgetSettings";
import AddressWidgetSettings from "./AddressWidgetSettings";
import ContactWidgetSettings from "./ContactWidgetSettings";
import SmartLinkWidgetSettings from "@/containers/PlayerCardSettings/Tabs/PlayerCardTab/Components/SmartLinkWidgetSettings";

interface WidgetSettingsProps {
  index: number;
  widget: PolymorphicPlayerCardWidget;
  onUpdate: (index: number, widget: PolymorphicPlayerCardWidget) => void;
  onRemove: (index: number) => void;
  isExpanded: boolean;
  isDragging: boolean;
  onClick: () => void;
  onViewMetrics?: () => void;
  onInsertWidget: () => void;
  onDragOver: (event: React.DragEvent) => void;
  onDragStart: (event: React.DragEvent) => void;
  onDragEnd: (event: React.DragEvent) => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

const WidgetSettings: React.FC<WidgetSettingsProps> = ({
  index,
  widget,
  onUpdate,
  onRemove,
  isExpanded,
  isDragging,
  onClick,
  onViewMetrics,
  onInsertWidget,
  onDragOver,
  onDragStart,
  onDragEnd,
  onMouseEnter,
  onMouseLeave,
}) => {
  const accordionRef = useRef<HTMLDivElement>(null);

  const {
    formState: { errors },
    getValues,
  } = useFormContext<PolymorphicPlayerCard>();
  const { User } = useContext(PlayerCardSettingsContext);

  const widgetConfigComponent = useMemo(() => {
    switch (widget.owner.type) {
      case "Header":
        return <HeaderWidgetSettings index={index} />;
      case "Footer":
        return <FooterWidgetSettings index={index} />;
      case "Link":
        return <LinkWidgetSettings index={index} />;
      case "SocialMedia":
        return <SocialMediaWidgetSettings index={index} />;
      case "ContentPage":
        return <ContentPageWidgetSettings index={index} />;
      case "Blog":
        return <BlogWidgetSettings index={index} />;
      case "Post":
        return <PostWidgetSettings index={index} />;
      case "SmartLink":
        return <SmartLinkWidgetSettings index={index} />;
      case "Phone":
        return <PhoneWidgetSettings index={index} />;
      case "Email":
        return <EmailWidgetSettings index={index} />;
      case "Address":
        return <AddressWidgetSettings index={index} />;
      case "Contact":
        return <ContactWidgetSettings index={index} />;
      default:
        return null;
    }
  }, [widget]);

  const isDraggable = User.hasWritePermission && !["Header", "Footer"].includes(widget.owner.type);
  var handleDragStart: React.DragEventHandler<HTMLDivElement> | undefined = undefined;
  var handleDragEnd: React.DragEventHandler<HTMLDivElement> | undefined = undefined;
  if (isDraggable) {
    handleDragStart = event => {
      if (accordionRef.current) {
        const rect = accordionRef.current.getBoundingClientRect();
        const offsetX = event.clientX - rect.left;
        const offsetY = event.clientY - rect.top;
        event.dataTransfer.setDragImage(accordionRef.current, offsetX, offsetY);
      }
      requestAnimationFrame(() => {
        onDragStart(event);
      });
    };
    handleDragEnd = event => {
      requestAnimationFrame(() => {
        onDragEnd(event);
      });
    };
  }

  const widgetTitle = getWidgetTitle(widget.owner.type);
  const widgetSubtitle = isExpanded ? null : getWidgetSubtitle(widget.owner);

  const classes = [];
  if (isDragging) classes.push("transparent");
  if (!widget.isVisible) classes.push("hiddenWidget");

  const generateWidgetAction = (icon: SemanticICONS, popupText: string, onClick: () => void, selected: boolean) => {
    const handleClick = (e: any) => {
      e.stopPropagation();
      onClick();
    };
    return (
      <Popup
        position={"top center"}
        trigger={<Icon className={`widgetAction ${selected ? "selected" : ""}`} name={icon} onClick={handleClick} />}
      >
        {popupText}
      </Popup>
    );
  };

  const hideWidget = () => {
    onUpdate(index, { ...widget, isVisible: false });
  };

  const order = getValues(`widgets.${index}.sortIndex`) ?? 0;

  return (
    <div
      className="widgetAccordionContainer"
      onDragOver={isDraggable ? onDragOver : undefined}
      ref={accordionRef}
      style={{ order }}
    >
      <Accordion className={classes.join(" ")} styled>
        <Accordion.Title
          className={errors?.widgets?.[index] ? "widgetError" : ""}
          active
          onClick={onClick}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          <Icon
            name="bars"
            className={`dragAndDropIcon ${isDraggable ? "isDraggable" : ""}`}
            draggable={isDraggable}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          />
          <span className="widgetTitle">{widgetTitle}</span>
          {widgetSubtitle && <span className="widgetSubtitle">{widgetSubtitle}</span>}
          <div className="grow" />
          {onViewMetrics &&
            generateWidgetAction(
              "chart bar",
              getCountMetricsText(widget.clickMetricsCount || 0, "Click"),
              onViewMetrics,
              false
            )}
          {isDraggable && (
            <>
              {User.hasWritePermission && generateWidgetAction("trash", "Delete", () => onRemove(index), false)}
              {User.hasWritePermission && widget.isVisible && generateWidgetAction("eye", "Hide", hideWidget, false)}
            </>
          )}
          {!widget.isVisible &&
            generateWidgetAction(
              "eye slash",
              "Unhide",
              () => {
                if (User.hasWritePermission) onUpdate(index, { ...widget, isVisible: true });
              },
              true
            )}
          <Icon name={`chevron ${isExpanded ? "down" : "right"}`} />
        </Accordion.Title>
        <Accordion.Content
          active={isExpanded}
          className={`widgetAccordionContainerContent ${isDragging ? "transparent" : ""}`}
        >
          {widgetConfigComponent}
        </Accordion.Content>
        {isDragging && <div className="dragBackground" />}
      </Accordion>
      {widget.owner.type !== "Footer" && (
        <div className={`insertWidget ${User.hasWritePermission ? "" : "readOnly"}`} onClick={onInsertWidget}>
          <div className="insertWidgetLine" />
          <Icon name="plus" />
          <div className="insertWidgetLine" />
        </div>
      )}
    </div>
  );
};
export default WidgetSettings;
